import React from "react";
import type { ApolloClient } from "@apollo/client";
import queryString from "query-string";
import { connect, type ConnectedProps } from "react-redux";
import {
  filter,
  find,
  get,
  groupBy,
  isEmpty,
  keyBy,
  size,
  some,
  transform,
} from "lodash";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { BsTrash } from "react-icons/bs";
import { Card } from "react-bootstrap";
import { push } from "store/actions/router";
import { setTab } from "actions/navigationActions";
import {
  hasAutoApprovalLinkRequestsFeature,
  hasExpiredInvites,
  hasEnabled360PayrollIntegration,
} from "store/selectors/employer";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import { toast } from "react-toastify";
import { payrollPeriodsToEnglishMapping } from "utils/timeHelper";
import {
  groupStatusToEnglish,
  groupStatusType,
} from "statics/statusToEnglishMapping";
import {
  getAllEmployerGroups,
  getLinkedEmployees,
  getPendingLinkedEmployees,
  updateEmployerGroup,
} from "actions/employerActions";
import {
  activeBanksSelector,
  banksSelector,
  selectableBanksSelector,
  isBankVerifiedSelector,
  isBankVerified,
  isBankPending,
} from "store/selectors/bank";
import {
  FiChevronDown,
  FiEdit3,
  FiGrid,
  FiPlus,
  FiUserPlus,
  FiUsers,
} from "react-icons/fi";

import AddGroup from "pages/dashboards/employerDashboard/dashboard/AddGroup";
import IconMessageCard from "components/IconMessageCard";
import EditGroupForm from "components/EditGroupForm";
import IconTable from "components/IconTable";
import IconSpinner from "components/IconSpinner";
import RemoveGroup from "components/RemoveGroup";
import GroupWithEmployees from "./GroupWithEmployees";
import Button from "components/Button";
import StatusBox from "components/StatusBox";

import "./EmployerGroups.scss";
import ActionsMenu from "components/ActionsMenu";
import type { RootState } from "types/state";
import type { UserBank } from "types/models";

const EMPLOYER_GROUP_LIMIT = 10;

// Status keys used to index the english/type mappings.
type GroupStatusKey = keyof typeof groupStatusToEnglish;

const isDeleting = createLoadingSelector(
  employerConstants.DELETE_EMPLOYER_GROUPS
);

const initialLoadingSelector = createLoadingSelector([
  employerConstants.GET_EMPLOYER_ACCOUNTS,
  employerConstants.GET_EMPLOYER_GROUPS,
]);

const groupUpdateActions = [employerConstants.UPDATE_EMPLOYER_GROUPS];
const groupUpdateErrorSelector = createErrorSelector(groupUpdateActions);
const groupUpdatingSelector = createLoadingSelector(groupUpdateActions);
const errorSelector = createErrorSelector(
  employerConstants.DELETE_EMPLOYER_GROUPS
);

const mapStateToProps = (state: RootState) => {
  const groups = state.employer.groups;
  const groupsById = keyBy(state.employer.groups, "id");

  let groupWithEmployees: Record<string, unknown> = groupBy(
    state.employer.linkedEmployees,
    "groupWithSchedule.group.id"
  );

  // transform over an array defaults its accumulator to []; the iteratee then
  // sets string keys on it (preserved). Cast the result to the object view the
  // consumers index into.
  groupWithEmployees = transform(
    state.employer.groups as Record<string, unknown>[],
    (acc: Record<string, unknown>, group: Record<string, unknown>) => {
      acc[group.id as string] = {
        ...group,
        employees: groupWithEmployees[group.id as string] || [],
      };

      return acc;
    }
  ) as Record<string, unknown>;
  const hasAutoApprovalFeature = hasAutoApprovalLinkRequestsFeature(state);

  const pendingLinks = hasAutoApprovalFeature
    ? []
    : filter(
        state.employer.employerLinkRequests,
        (request: Record<string, unknown>) =>
          request.status === "REQUEST_ACCESS"
      );

  const employerLinkRequests = groupBy(pendingLinks, "group.id");

  return {
    groups,
    isDeleting: isDeleting(state),
    error: errorSelector(state),
    groupUpdateError: groupUpdateErrorSelector(state),
    groupUpdating: groupUpdatingSelector(state),
    hasBank: isBankVerifiedSelector(state),
    accounts: activeBanksSelector(state),
    hasExpiredInvites: hasExpiredInvites(state),
    isFetching: initialLoadingSelector(state),
    bankAccounts: banksSelector(state),
    selectableAccounts: selectableBanksSelector(state),
    hasGroup: !isEmpty(state.employer.groups),
    planId: state.employer.company.planId,
    groupsById,
    groupWithEmployees,
    employerLinkRequests,
    has360PayrollIntegration: hasEnabled360PayrollIntegration(state),
  };
};

const mapDispatchToProps = {
  push,
  setTab,
  getAllEmployerGroups,
  updateEmployerGroup,
  getLinkedEmployees,
  getPendingLinkedEmployees,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // Read in render() but never supplied by redux or the sole caller
  // (<EmployerGroups /> in CompanyProfile passes no props) — undefined at runtime.
  hasPayrollIntegration?: boolean;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  initialFetching: boolean;
  showGroupForm: boolean;
  showEditmodal: boolean;
  addGroupActive: boolean;
  showRemoveModal: boolean;
  showGroupWithEmployees: boolean;
  groupData: Record<string, unknown> | null;
  groupWithEmployees: unknown;
  groupForEdit: Record<string, unknown>;
  groupId: string;
}

class EmployerGroups extends React.Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    const groupId = queryString.parse(window.location.search).groupId;

    this.state = {
      initialFetching: true,
      showGroupForm: false,
      showEditmodal: false,
      addGroupActive: false,
      showRemoveModal: false,
      showGroupWithEmployees: false,
      groupData: null,
      groupWithEmployees: null,
      groupForEdit: {},
      groupId: (groupId as string) || "",
    };
  }

  async componentDidMount() {
    window.analytics.page("Employer Approvals");

    await Promise.all([
      this.props.getPendingLinkedEmployees(
        this.props.client as ApolloClient<object>
      ),
      this.props.getLinkedEmployees(this.props.client as ApolloClient<object>),
      this.props.getAllEmployerGroups(
        this.props.client as ApolloClient<object>
      ),
    ]);

    this.setState({ initialFetching: false });
  }

  _submitGroupForEdit = (group: Record<string, unknown>) => {
    void this.props
      // updateEmployerGroup's typed signature also requires a `companyId` third
      // arg, but this call has always passed only (client, group) — preserved.
      // @ts-expect-error pre-existing 2-arg call against a 3-arg action creator
      .updateEmployerGroup(this.props.client as ApolloClient<object>, group)
      .then((result) => {
        if (result.success) {
          toast.success("Successfully updated group.");
          this.setState({ showEditmodal: false, groupForEdit: {} });
        }
      });
  };

  _actionBtn = () => {
    if (this.props.hasBank) {
      return (
        <Button
          icon={{
            icon: <FiPlus size={14} color={"#fffff"} />,
            position: "left",
          }}
          size="sm"
          name="action"
          onClick={() => this.setState({ showGroupForm: true })}
          btnLabel="Add Group"
        />
      );
    } else {
      return (
        <Button
          icon={{
            icon: <FiPlus size={14} color={"#fffff"} />,
            position: "left",
          }}
          size="sm"
          name="action"
          onClick={() => {
            document.body.click();
            this.props.push("/dashboard");
          }}
          btnLabel="Add Bank"
        />
      );
    }
  };

  _addGroup() {
    const numberOfGroups = size(this.props.groups);
    return (
      <div>
        <div className="widget-header">
          <span>
            <Card.Title>
              Groups:
              <p className="invite-number">
                Active groups (<p className="number">{numberOfGroups}</p>)
              </p>
            </Card.Title>
          </span>
          <div>{this._actionBtn()}</div>
        </div>
      </div>
    );
  }

  _blankSlate = () => {
    let header;

    if (!this.props.hasBank) {
      header = "Please add a bank account before you configure your groups.";
    } else if (this.props.hasBank) {
      header = "Add a group to get started.";
    }
    return (
      <>
        {this._addGroup()}
        <IconMessageCard
          header={header}
          icon={<FiUsers color="white" stroke="#60A4BF" size={16} />}
        />
      </>
    );
  };

  getPopoverActions = (
    groupIsDeletable: boolean,
    group: Record<string, unknown>
  ) => {
    const actions = [];

    if (!this.props.has360PayrollIntegration) {
      actions.push({
        label: "Edit Group",
        icon: <FiEdit3 size={14} color={"#60A4BF"} />,
        onClick: () => {
          this.setState({
            showEditmodal: true,
            groupForEdit: group,
          });
        },
      });
    }

    if (!groupIsDeletable) {
      actions.push({
        label: "View Group",
        icon: <FiGrid color="white" stroke="#60A4BF" size={14} />,
        onClick: () => {
          this.setState({
            showGroupWithEmployees: true,
            groupWithEmployees: get(
              this.props.groupWithEmployees,
              group.id as string,
              []
            ),
          });
        },
      });
    } else {
      actions.push({
        label: "Add Employee",
        icon: <FiUserPlus size={14} color={"#60A4BF"} />,
        onClick: () => {
          this.props.push("/dashboard/users/employees");
        },
      });

      if (!this.props.has360PayrollIntegration) {
        actions.push({
          label: "Remove",
          icon: <BsTrash size={14} color="#B12121" />,
          onClick: () => {
            this.setState({
              showRemoveModal: true,
              groupData: group,
            });
          },
        });
      }
    }

    return actions;
  };

  render() {
    let content;

    if (this.props.isFetching) {
      return <IconSpinner centered />;
    }

    if (isEmpty(this.props.groups)) {
      content = this._blankSlate();
    } else {
      const columns = [
        {
          key: "name",
          label: "Group",
        },
        {
          label: "Plan Id",
          customComponent: (props: Record<string, unknown>) => {
            const planId = `${String(this.props.planId)}-${String(
              props.companyGroupIndex
            )}`;
            return planId;
          },
        },

        {
          label: "Bank Account",
          customComponent: (props: Record<string, unknown>) => {
            const bankAccounts = this.props.bankAccounts;
            const associatedBankAccountId = get(
              props.associatedBankAccount,
              "id",
              null
            );

            const associatedBankAccount = find(bankAccounts, {
              id: associatedBankAccountId,
            });
            const accountId = get(associatedBankAccount, "accountId");
            if (!accountId) return "";

            const associatedBankAccountProp =
              props.associatedBankAccount as Record<string, unknown>;
            const planId = `${String(associatedBankAccountProp.bankName)} ${String(
              accountId
            )}`;
            return planId;
          },
        },
        {
          key: "payrollFrequency",
          label: "Payroll Frequency",
          customComponent: (props: Record<string, unknown>) => (
            <>
              {payrollPeriodsToEnglishMapping[
                props.payrollFrequency as keyof typeof payrollPeriodsToEnglishMapping
              ] || String(props.payrollFrequency)}
            </>
          ),
        },

        {
          key: "associatedLinkRequests",
          label: "Participants",
          customComponent: (props: Record<string, unknown>) => {
            const linkedEmployees = filter(
              props.associatedLinkRequests as Record<string, unknown>[],
              {
                status: "ACTIVE",
              }
            );
            return <>{size(linkedEmployees)}</>;
          },
        },
        {
          label: "Status",
          customComponent: (props: Record<string, unknown>) => {
            const linkRequests = props.associatedLinkRequests as Record<
              string,
              unknown
            >[];
            const requestAccess = "REQUEST_ACCESS";
            const pendingUsers = some(linkRequests, {
              status: requestAccess,
            });
            const bankId = (
              props.associatedBankAccount as Record<string, unknown>
            ).id;
            const associatedBankAccount = find(this.props.bankAccounts, {
              id: bankId as string,
            });
            const bankIsVerified = isBankVerified(
              associatedBankAccount as UserBank | null | undefined
            );
            const bankIsPending = isBankPending(
              associatedBankAccount as UserBank | null | undefined
            );
            const active = !isEmpty(linkRequests) && bankIsVerified;
            const notActive = isEmpty(linkRequests) && bankIsVerified;

            let status: GroupStatusKey | undefined;
            if (pendingUsers) {
              status = "REQUEST_ACCESS";
            } else if (active) {
              status = "ACTIVE";
            } else if (notActive) {
              status = "NOT_ACTIVE";
            } else if (bankIsPending) {
              status = "LINK_BANK_PROCESSING";
            } else if (!bankIsVerified && !bankIsPending) {
              status = "LINK_BANK";
            }
            return (
              <>
                <StatusBox
                  status={status ? groupStatusToEnglish[status] : undefined}
                  type={
                    status
                      ? groupStatusType[status as keyof typeof groupStatusType]
                      : undefined
                  }
                />
              </>
            );
          },
        },

        {
          label: "Actions",
          customComponent: (props: Record<string, unknown>) => {
            const groupIsDeletable = isEmpty(props.associatedLinkRequests);
            const groupId = props.id;

            const linkRequests = props.associatedLinkRequests as Record<
              string,
              unknown
            >[];
            const requestAccess = "REQUEST_ACCESS";
            const pendingUsers = some(linkRequests, {
              status: requestAccess,
            });

            const bankId = (
              props.associatedBankAccount as Record<string, unknown>
            ).id;
            const associatedBankAccount = find(this.props.bankAccounts, {
              id: bankId as string,
            });
            const bankIsVerified = isBankVerified(
              associatedBankAccount as UserBank | null | undefined
            );
            const bankIsPending = isBankPending(
              associatedBankAccount as UserBank | null | undefined
            );
            const verifiedOrPendingBankExists = some(
              this.props.bankAccounts,
              (account) => isBankVerified(account) || isBankPending(account)
            );

            const updateGroupBank =
              !(bankIsVerified || bankIsPending) && verifiedOrPendingBankExists;
            // Including bankIsVerified here gives priority to having a valid bank before showing pending user review
            const showPendingUserReview = pendingUsers && bankIsVerified;

            const removedAllBankAccounts =
              !verifiedOrPendingBankExists && this.props.hasGroup;

            if (this.props.isFetching) {
              return <IconSpinner centered />;
            }

            if (showPendingUserReview) {
              return (
                <>
                  <Button
                    className="table-btn"
                    color="action"
                    name="action"
                    size="sm"
                    btnLabel="Review"
                    onClick={() => {
                      this.props.push({
                        pathname: `/dashboard/company/group/${String(
                          groupId
                        )}/review-employees`,
                      });
                    }}
                  />
                </>
              );
            } else if (updateGroupBank) {
              return (
                <>
                  <Button
                    className="table-btn"
                    color="action"
                    name="action"
                    size="sm"
                    btnLabel="Update Group Bank"
                    onClick={() => {
                      this.setState({
                        showEditmodal: true,
                        groupForEdit: props,
                      });
                    }}
                  />
                </>
              );
            } else if (removedAllBankAccounts) {
              return (
                <>
                  <Button
                    className="table-btn"
                    color="action"
                    name="action"
                    size="sm"
                    btnLabel="Link New Bank"
                    onClick={() => {
                      this.props.push({
                        pathname: `/dashboard/company/bank`,
                      });
                    }}
                  />
                </>
              );
            } else {
              return (
                <>
                  <div className="groups-table-actions">
                    <ActionsMenu
                      actions={this.getPopoverActions(groupIsDeletable, props)}
                    >
                      {/* @ts-expect-error Button requires `name`; this usage has always omitted it (preserved) */}
                      <Button color={"action"}>
                        <div className="group-actions-dropdown">
                          <p className="group-actions-label">Actions</p>
                          <span>
                            <FiChevronDown
                              color="white"
                              stroke={"#1b9aa9"}
                              size={10}
                            />
                          </span>
                        </div>
                      </Button>
                    </ActionsMenu>
                  </div>
                </>
              );
            }
          },
        },
      ];

      content = (
        <>
          {this.props.groups.length < EMPLOYER_GROUP_LIMIT && this._addGroup()}
          <IconTable columns={columns} data={this.props.groups} />
        </>
      );
    }

    if (this.state.showGroupForm) {
      return (
        <AddGroup onClose={() => this.setState({ showGroupForm: false })} />
      );
    } else if (this.state.showEditmodal) {
      return (
        <div className="download-employee-template">
          <div className="main-content">
            <Card>
              <Card.Title>
                <p className="page-header">Edit Group</p>
              </Card.Title>
              <Card.Body>
                <EditGroupForm
                  // groupForEdit is an untyped state bag; EditGroupForm wants
                  // its concrete Group / GroupSubmitValues shapes — cast at the
                  // boundary (runtime value unchanged).
                  group={
                    this.state.groupForEdit as unknown as React.ComponentProps<
                      typeof EditGroupForm
                    >["group"]
                  }
                  onClose={() => {
                    this.setState({ showEditmodal: false, groupForEdit: {} });
                  }}
                  onSubmit={
                    this._submitGroupForEdit as unknown as React.ComponentProps<
                      typeof EditGroupForm
                    >["onSubmit"]
                  }
                  error={this.props.groupUpdateError}
                  accounts={this.props.selectableAccounts}
                  isLoading={this.props.groupUpdating}
                />
              </Card.Body>
            </Card>
          </div>
        </div>
      );
    } else if (this.state.showGroupWithEmployees) {
      return (
        <>
          <Card className="groups-table">
            <GroupWithEmployees
              // groupWithEmployees state is untyped (unknown); planId comes from
              // the still-shallow employer.company slice (unknown) — cast at the
              // boundary, runtime values unchanged.
              groupWithEmployees={
                this.state
                  .groupWithEmployees as unknown as React.ComponentProps<
                  typeof GroupWithEmployees
                >["groupWithEmployees"]
              }
              planId={this.props.planId as string | undefined}
              // GroupWithEmployees no longer declares a `groupId` prop, but the
              // original always passed one; preserved (React ignores it).
              // @ts-expect-error excess prop kept for runtime fidelity
              groupId={this.state.groupId}
              hasPayrollIntegration={this.props.hasPayrollIntegration}
              onClose={() => {
                this.setState({
                  showGroupWithEmployees: false,
                  groupWithEmployees: null,
                });
              }}
            />
          </Card>
        </>
      );
    }

    return (
      <>
        {this.state.showRemoveModal && (
          <>
            <RemoveGroup
              // RemoveGroup's prop type declares an array, but this has always
              // passed a single group object (RemoveGroup reads it via lodash
              // `get(..., "id")`) — cast at the boundary, runtime unchanged.
              groupData={
                this.state.groupData as unknown as React.ComponentProps<
                  typeof RemoveGroup
                >["groupData"]
              }
              onClose={() =>
                this.setState({
                  showRemoveModal: false,
                  groupData: null,
                })
              }
              onSuccess={() => {
                void this.props.getAllEmployerGroups(
                  this.props.client as ApolloClient<object>
                );
                this.setState({ showRemoveModal: false });
              }}
            />
          </>
        )}

        <div>
          <Card className="groups-table">{content}</Card>
        </div>
      </>
    );
  }
}

export default connector(withApollo<Props>(EmployerGroups));
